import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { clientsAPI, miniPellesAPI, optionsAPI, devisAPI, categoriesAPI, cabinetsSNCAPI, aiAPI, serviceCatalogAPI } from '@/lib/api';
import { formatPrice, cn, getImageUrl } from '@/lib/utils';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { toast } from 'sonner';
import {
  ArrowLeft,
  ArrowRight,
  Check,
  ChevronsUpDown,
  Package,
  Users,
  Wrench,
  FileText,
  Loader2,
  Plus,
  Calculator,
  Building2,
  Sparkles,
  Search,
  LayoutGrid,
  List,
} from 'lucide-react';
import { ServiceQuoteEditor, createEmptyServiceLine, normalizeServiceLines } from '@/pages/ServiceQuoteShared';

const steps = [
  { id: 1, name: 'Client', icon: Users },
  { id: 2, name: 'Machine', icon: Package },
  { id: 3, name: 'Options', icon: Wrench },
  { id: 4, name: 'Financement', icon: Calculator },
  { id: 5, name: 'Récapitulatif', icon: FileText },
];

const DevisCreatePage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const preselectedClientId = searchParams.get('client');
  const initialQuoteType = searchParams.get('mode') === 'service' ? 'service' : 'machine';
  
  const [currentStep, setCurrentStep] = useState(1);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  
  const [clients, setClients] = useState([]);
  const [miniPelles, setMiniPelles] = useState([]);
  const [options, setOptions] = useState([]);
  const [categories, setCategories] = useState([]);
  const [cabinetsSNC, setCabinetsSNC] = useState([]);
  const [banques, setBanques] = useState([]); // Contacts de type "banque"
  const [serviceCatalog, setServiceCatalog] = useState([]);
  
  // Search filter for clients
  const [clientSearch, setClientSearch] = useState('');
  
  // Brand filter for machines
  const [selectedBrand, setSelectedBrand] = useState('all');
  
  // Apport client pour simulation SNC
  const [apportClientPercent, setApportClientPercent] = useState(30);
  
  // View mode for machines
  const [machineViewMode, setMachineViewMode] = useState('dropdown'); // 'dropdown', 'grid' or 'list' - default to dropdown
  
  // AI dialog
  const [aiDialogOpen, setAiDialogOpen] = useState(false);
  const [aiText, setAiText] = useState('');
  const [aiLoading, setAiLoading] = useState(false);
  
  // Client creation dialog
  const [clientDialogOpen, setClientDialogOpen] = useState(false);
  const [clientFormLoading, setClientFormLoading] = useState(false);
  const [clientForm, setClientForm] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    address: '',
    notes: '',
    client_type: '',
  });
  
  const [form, setForm] = useState({
    devis_type: initialQuoteType,
    client_id: preselectedClientId || '',
    mini_pelle_id: '',
    selected_variant_id: '', // ID de la variante sélectionnée
    selected_options: [],
    discount: 0,
    discount_type: 'percent',
    notes: '',
    validity_days: 30,
    show_snc: initialQuoteType !== 'service', // Checkbox for SNC
    cabinet_snc_id: '',
    selected_banques: [], // Multiple banques [{id, name, email}]
    apport_client_percent: 10, // Apport client en %
    service_lines: [createEmptyServiceLine()],
  });

  // Filter clients based on search
  const filteredClients = clients.filter((c) => {
    if (!clientSearch.trim()) return true;
    const search = clientSearch.toLowerCase();
    return (
      c.name.toLowerCase().includes(search) ||
      (c.email && c.email.toLowerCase().includes(search)) ||
      (c.phone && c.phone.includes(search)) ||
      (c.company && c.company.toLowerCase().includes(search))
    );
  });

  useEffect(() => {
    fetchData();
  }, []);

  // Handle Enter key for step navigation
  const handleKeyDown = useCallback((e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      const isTextarea = e.target.tagName === 'TEXTAREA';
      const isDialog = e.target.closest('[role="dialog"]');
      if (!isTextarea && !isDialog && currentStep < 4) {
        e.preventDefault();
        if (canProceed()) {
          setCurrentStep(currentStep + 1);
        }
      }
    }
  }, [currentStep]);

  useEffect(() => {
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [handleKeyDown]);

  const canProceed = () => {
    if (currentStep === 1) return !!form.client_id;
    if (form.devis_type === 'service') return true;
    if (currentStep === 2) return !!form.mini_pelle_id;
    return true;
  };

  const fetchData = async () => {
    try {
      const [clientsRes, mpRes, optRes, catRes, cabRes, banquesRes, serviceCatalogRes] = await Promise.all([
        clientsAPI.getAll(),
        miniPellesAPI.getAll(),
        optionsAPI.getAll(),
        categoriesAPI.getAll(),
        cabinetsSNCAPI.getAll(),
        clientsAPI.getByType('banque').catch(() => ({ data: [] })),
        serviceCatalogAPI.getAll().catch(() => ({ data: [] })),
      ]);
      setClients(clientsRes.data);
      setMiniPelles(mpRes.data);
      setOptions(optRes.data);
      setCategories(catRes.data);
      setCabinetsSNC(cabRes.data);
      setBanques(banquesRes.data || []);
      setServiceCatalog(serviceCatalogRes.data || []);
    } catch (error) {
      toast.error('Erreur lors du chargement des données');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateClient = async (e) => {
    e.preventDefault();
    setClientFormLoading(true);
    
    try {
      const response = await clientsAPI.create(clientForm);
      toast.success('Client créé avec succès');
      setClients([...clients, response.data]);
      setForm({ ...form, client_id: response.data.id });
      setClientDialogOpen(false);
      setClientForm({
        name: '',
        email: '',
        phone: '',
        company: '',
        address: '',
        notes: '',
        client_type: '',
      });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la création');
    } finally {
      setClientFormLoading(false);
    }
  };

  const handleAICreate = async () => {
    if (!aiText.trim()) {
      toast.error('Veuillez coller les informations');
      return;
    }
    
    setAiLoading(true);
    try {
      const response = await aiAPI.createFromLead(aiText);
      const result = response.data;
      
      // Refresh clients and select the new one
      const clientsRes = await clientsAPI.getAll();
      setClients(clientsRes.data);
      setForm({ ...form, client_id: result.client_id });
      
      toast.success(`Client "${result.client_name}" créé avec succès !`);
      setAiDialogOpen(false);
      setAiText('');
      
      // If a machine was identified, preselect it
      if (result.machine_name) {
        const machine = miniPelles.find(mp => mp.name.toLowerCase().includes(result.machine_name.toLowerCase()));
        if (machine) {
          setForm(prev => ({ ...prev, mini_pelle_id: machine.id }));
          toast.info(`Machine "${machine.name}" pré-sélectionnée`);
        }
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur IA');
    } finally {
      setAiLoading(false);
    }
  };

  const selectedClient = clients.find((c) => c.id === form.client_id);
  const selectedMiniPelle = miniPelles.find((mp) => mp.id === form.mini_pelle_id);
  const [machinePopoverOpen, setMachinePopoverOpen] = useState(false);

  // Formater le tonnage/poids pour l'affichage
  const formatTonnage = (mp) => {
    if (!mp.weight) return null;
    const w = String(mp.weight).replace(',', '.').trim();
    const num = parseFloat(w);
    if (isNaN(num)) return null;
    if (num < 10) return `${w.replace('.', ',')}T`;   // ex: 1,7T
    return `${num >= 1000 ? (num/1000).toFixed(1).replace('.', ',') + 'T' : num + ' kg'}`;
  };
  const selectedCabinetSNC = cabinetsSNC.find((c) => c.id === form.cabinet_snc_id);
  
  // Get selected variant if any
  const selectedVariant = selectedMiniPelle?.has_variants && form.selected_variant_id
    ? selectedMiniPelle.variants?.find(v => v.id === form.selected_variant_id)
    : null;
  
  // Get effective price and image (variant overrides base)
  const effectivePrice = selectedVariant ? selectedVariant.price : (selectedMiniPelle?.base_price || 0);
  const effectiveImage = selectedVariant?.image_url || selectedMiniPelle?.image_url;
  const effectiveName = selectedVariant 
    ? `${selectedMiniPelle?.name} - ${selectedVariant.name}` 
    : selectedMiniPelle?.name;
  
  // Get option compatibility and price for the selected machine
  const getOptionCompatibility = (option) => {
    if (!form.mini_pelle_id) return { compatible: true, price: 0, included: false };
    
    // Check new compatibility_by_model first
    const compat = option.compatibility_by_model?.[form.mini_pelle_id];
    if (compat) {
      return {
        compatible: compat.compatible !== false,
        price: compat.included ? 0 : (compat.price || 0),
        included: compat.included || false,
      };
    }
    
    // Fall back to legacy prices_by_model
    const legacyPrice = option.prices_by_model?.[form.mini_pelle_id];
    if (legacyPrice !== undefined) {
      return { compatible: true, price: legacyPrice, included: false };
    }
    
    // If not defined, assume compatible with price 0 (for backward compatibility)
    return { compatible: true, price: 0, included: false };
  };
  
  const getOptionPrice = (option) => {
    const compat = getOptionCompatibility(option);
    return compat.price;
  };
  
  const isOptionCompatible = (option) => {
    const compat = getOptionCompatibility(option);
    return compat.compatible;
  };
  
  const isOptionIncluded = (option) => {
    const compat = getOptionCompatibility(option);
    return compat.included;
  };

  const toggleOption = (option) => {
    const isSelected = form.selected_options.some((o) => o.option_id === option.id);
    
    if (isSelected) {
      setForm({
        ...form,
        selected_options: form.selected_options.filter((o) => o.option_id !== option.id),
      });
    } else {
      setForm({
        ...form,
        selected_options: [
          ...form.selected_options,
          {
            option_id: option.id,
            option_name: option.name,
            category: option.category,
            price: getOptionPrice(option),
            image_url: option.image_url,
          },
        ],
      });
    }
  };

  const calculateTotals = () => {
    const miniPellePrice = selectedMiniPelle?.base_price || 0;
    const optionsTotal = form.selected_options.reduce((sum, opt) => sum + opt.price, 0);
    const subtotal = miniPellePrice + optionsTotal;
    
    let discountAmount = 0;
    if (form.discount_type === 'percent') {
      discountAmount = subtotal * (form.discount / 100);
    } else {
      discountAmount = form.discount;
    }
    
    const total = subtotal - discountAmount;
    
    return { miniPellePrice, optionsTotal, subtotal, discountAmount, total };
  };

  // Calcul simulation SNC avec apport client
  const calculateSNC = (total) => {
    const apportSNC = total * 0.25; // Apport SNC fixe 25%
    const apportClient = total * (form.apport_client_percent / 100);
    const totalApport = apportSNC + apportClient;
    const totalFinance = total - totalApport;
    const loyerMensuel = (totalFinance / 60) * 1.2;
    return { apportSNC, apportClient, totalApport, totalFinance, loyerMensuel };
  };

  const handleSubmit = async () => {
    if (!form.client_id) {
      toast.error('Veuillez sélectionner un client');
      return;
    }

    if (form.devis_type === 'service') {
      const serviceLines = normalizeServiceLines(form.service_lines).filter((line) => line.designation.trim());
      if (serviceLines.length === 0) {
        toast.error('Ajoute au moins une ligne de prestation ou de pièce');
        return;
      }

      setSubmitting(true);

      try {
        const response = await devisAPI.create({
          devis_type: 'service',
          client_id: form.client_id,
          client_name: selectedClient.name,
          client_email: selectedClient.email,
          client_phone: selectedClient.phone,
          service_lines: serviceLines,
          discount: form.discount,
          discount_type: form.discount_type,
          notes: form.notes,
          validity_days: form.validity_days,
          status: 'brouillon',
          show_snc: false,
          show_financement: false,
          banques: [],
          apport_client_percent: 0,
        });
        toast.success('Devis prestation créé avec succès');
        navigate(`/admin/devis/${response.data.id}`);
      } catch (error) {
        toast.error(error.response?.data?.detail || 'Erreur lors de la création');
      } finally {
        setSubmitting(false);
      }
      return;
    }

    if (!form.mini_pelle_id) {
      toast.error('Veuillez sélectionner un client et une machine');
      return;
    }
    
    setSubmitting(true);
    
    const totals = calculateTotals();
    
    // Préparer les specs de la machine
    const miniPelleSpecs = {
      weight: selectedMiniPelle.weight,
      longueur: selectedMiniPelle.longueur,
      largeur: selectedMiniPelle.largeur,
      profondeur_fouille: selectedMiniPelle.profondeur_fouille,
      hauteur_chargement: selectedMiniPelle.hauteur_chargement,
      marque_moteur: selectedMiniPelle.marque_moteur,
      nombre_cylindres: selectedMiniPelle.nombre_cylindres,
      puissance_moteur: selectedMiniPelle.puissance_moteur,
      marque_pompe: selectedMiniPelle.marque_pompe,
      debit_pompe: selectedMiniPelle.debit_pompe,
      brochure_url: selectedMiniPelle.brochure_url,
    };
    
    const devisData = {
      client_id: form.client_id,
      client_name: selectedClient.name,
      mini_pelle_id: form.mini_pelle_id,
      mini_pelle_name: effectiveName,
      mini_pelle_price: effectivePrice,
      mini_pelle_image: effectiveImage,
      mini_pelle_description: selectedMiniPelle.description,
      mini_pelle_specs: miniPelleSpecs,
      selected_variant_id: form.selected_variant_id || null,
      selected_variant_name: selectedVariant?.name || null,
      options: form.selected_options,
      discount: form.discount,
      discount_type: form.discount_type,
      notes: form.notes,
      validity_days: form.validity_days,
      status: 'brouillon',
      show_snc: form.show_snc,
      cabinet_snc_id: selectedCabinetSNC?.id || null,
      cabinet_snc_name: selectedCabinetSNC?.name || null,
      cabinet_snc_email: selectedCabinetSNC?.email || null,
      banques: form.selected_banques,
      apport_client_percent: form.apport_client_percent,
    };
    
    try {
      const response = await devisAPI.create(devisData);
      toast.success('Devis créé avec succès');
      navigate(`/admin/devis/${response.data.id}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la création');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-slate-800"></div>
      </div>
    );
  }

  if (form.devis_type === 'service') {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-end">
          <div className="inline-flex rounded-lg border border-slate-200 bg-white p-1">
            <Button type="button" variant="ghost" onClick={() => setForm((prev) => ({ ...prev, devis_type: 'machine' }))}>
              Devis machine
            </Button>
            <Button type="button" className="bg-slate-800 hover:bg-slate-800 text-white">
              Devis prestation
            </Button>
          </div>
        </div>
        <ServiceQuoteEditor
          title="Nouveau devis prestation"
          subtitle="Peinture, entretien, révision, réparation, pièces et main d'oeuvre"
          clients={clients}
          serviceCatalog={serviceCatalog}
          form={form}
          setForm={setForm}
          onBack={() => navigate('/admin/devis')}
          onSubmit={handleSubmit}
          submitting={submitting}
          submitLabel="Créer le devis"
        />
      </div>
    );
  }

  const totals = calculateTotals();

  return (
    <div className="space-y-6" data-testid="devis-create-page">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="icon" onClick={() => navigate('/admin/devis')}>
          <ArrowLeft className="w-5 h-5" />
        </Button>
        <div className="flex-1">
          <h1 className="text-3xl font-bold text-slate-800" style={{ fontFamily: 'Barlow Condensed' }}>
            Nouveau devis
          </h1>
          <p className="text-slate-500 mt-1">Créez un nouveau devis en quelques étapes</p>
        </div>
        <div className="inline-flex rounded-lg border border-slate-200 bg-white p-1">
          <Button type="button" className="bg-slate-800 hover:bg-slate-800 text-white">
            Devis machine
          </Button>
          <Button
            type="button"
            variant="ghost"
            onClick={() => setForm((prev) => ({ ...prev, devis_type: 'service', show_snc: false, selected_banques: [] }))}
          >
            Devis prestation
          </Button>
        </div>
        {/* SNC Toggle in header */}
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-2 px-3 py-2 bg-yellow-50 rounded-lg border border-yellow-200">
            <Calculator className="w-4 h-4 text-yellow-600" />
            <Label htmlFor="snc-toggle" className="text-sm font-medium text-yellow-700">
              Simulation SNC
            </Label>
            <Switch
              id="snc-toggle"
              checked={form.show_snc}
              onCheckedChange={(checked) => setForm({ ...form, show_snc: checked, cabinet_snc_id: checked ? form.cabinet_snc_id : '' })}
              data-testid="snc-toggle"
            />
          </div>
          {/* Cabinet SNC selector - only visible when SNC is enabled */}
          {form.show_snc && cabinetsSNC.length > 0 && (
            <div className="flex items-center gap-2">
              <Building2 className="w-4 h-4 text-yellow-600" />
              <Select
                value={form.cabinet_snc_id || "none"}
                onValueChange={(value) => setForm({ ...form, cabinet_snc_id: value === "none" ? "" : value })}
              >
                <SelectTrigger className="w-48 h-9 border-yellow-200 bg-yellow-50" data-testid="cabinet-snc-select">
                  <SelectValue placeholder="Cabinet SNC (optionnel)" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Aucun cabinet</SelectItem>
                  {cabinetsSNC.map((cabinet) => (
                    <SelectItem key={cabinet.id} value={cabinet.id}>
                      {cabinet.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
        </div>
      </div>

      {/* Stepper */}
      <div className="flex items-center justify-center">
        <div className="flex items-center gap-2">
          {steps.map((step, index) => (
            <React.Fragment key={step.id}>
              <button
                onClick={() => step.id < currentStep && setCurrentStep(step.id)}
                className={cn(
                  'flex items-center gap-2 px-4 py-2 rounded-md transition-colors',
                  currentStep === step.id
                    ? 'bg-slate-800 text-white'
                    : currentStep > step.id
                    ? 'bg-green-100 text-green-700'
                    : 'bg-slate-100 text-slate-400'
                )}
                disabled={step.id > currentStep}
                data-testid={`step-${step.id}`}
              >
                {currentStep > step.id ? (
                  <Check className="w-4 h-4" />
                ) : (
                  <step.icon className="w-4 h-4" />
                )}
                <span className="hidden sm:inline text-sm font-medium">{step.name}</span>
              </button>
              {index < steps.length - 1 && (
                <div className={cn(
                  'w-8 h-0.5',
                  currentStep > step.id ? 'bg-green-300' : 'bg-slate-200'
                )} />
              )}
            </React.Fragment>
          ))}
        </div>
      </div>

      {/* Step Content */}
      <Card className="border-slate-200">
        <CardContent className="p-6">
          {/* Step 1: Client */}
          {currentStep === 1 && (
            <div className="space-y-4" data-testid="step-client">
              <div className="flex items-center justify-between flex-wrap gap-3">
                <h2 className="text-xl font-semibold text-slate-800" style={{ fontFamily: 'Barlow Condensed' }}>
                  Sélectionner un client
                </h2>
                
                <div className="flex items-center gap-2">
                  {/* AI Creation Dialog */}
                  <Dialog open={aiDialogOpen} onOpenChange={setAiDialogOpen}>
                    <DialogTrigger asChild>
                      <Button variant="outline" size="sm" className="border-yellow-300 text-yellow-700 hover:bg-yellow-50" data-testid="ai-create-btn">
                        <Sparkles className="w-4 h-4 mr-2" />
                        Créer par IA
                      </Button>
                    </DialogTrigger>
                    <DialogContent className="sm:max-w-lg">
                      <DialogHeader>
                        <DialogTitle style={{ fontFamily: 'Barlow Condensed' }} className="flex items-center gap-2">
                          <Sparkles className="w-5 h-5 text-yellow-500" />
                          Création par IA
                        </DialogTitle>
                      </DialogHeader>
                      <div className="space-y-4">
                        <p className="text-sm text-slate-600">
                          Collez les informations d'un formulaire Facebook, email ou notes. L'IA créera automatiquement le client.
                        </p>
                        <Textarea
                          value={aiText}
                          onChange={(e) => setAiText(e.target.value)}
                          placeholder="Nom: Jean Dupont&#10;Email: jean@email.com&#10;Téléphone: 0692 XX XX XX&#10;Message: Intéressé par..."
                          rows={8}
                          className="font-mono text-sm"
                          data-testid="ai-text-input"
                        />
                        <div className="flex justify-end gap-3">
                          <Button type="button" variant="outline" onClick={() => setAiDialogOpen(false)}>
                            Annuler
                          </Button>
                          <Button
                            onClick={handleAICreate}
                            disabled={aiLoading || !aiText.trim()}
                            className="bg-yellow-500 hover:bg-yellow-600 text-slate-900"
                            data-testid="ai-submit-btn"
                          >
                            {aiLoading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                            <Sparkles className="w-4 h-4 mr-2" />
                            Créer le client
                          </Button>
                        </div>
                      </div>
                    </DialogContent>
                  </Dialog>
                
                  {/* Client Creation Dialog */}
                  <Dialog open={clientDialogOpen} onOpenChange={setClientDialogOpen}>
                    <DialogTrigger asChild>
                      <Button variant="outline" size="sm" data-testid="new-client-btn">
                        <Plus className="w-4 h-4 mr-2" />
                        Nouveau contact
                      </Button>
                    </DialogTrigger>
                    <DialogContent className="sm:max-w-md">
                      <DialogHeader>
                        <DialogTitle style={{ fontFamily: 'Barlow Condensed' }}>
                          Créer un nouveau client
                        </DialogTitle>
                      </DialogHeader>
                      <form onSubmit={handleCreateClient} className="space-y-4">
                      <div className="space-y-2">
                        <Label htmlFor="client-name">Nom *</Label>
                        <Input
                          id="client-name"
                          value={clientForm.name}
                          onChange={(e) => setClientForm({ ...clientForm, name: e.target.value })}
                          required
                          data-testid="new-client-name-input"
                        />
                      </div>
                      <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label htmlFor="client-company">Entreprise</Label>
                          <Input
                            id="client-company"
                            value={clientForm.company}
                            onChange={(e) => setClientForm({ ...clientForm, company: e.target.value })}
                            data-testid="new-client-company-input"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="client-type">Type de client</Label>
                          <Select
                            value={clientForm.client_type}
                            onValueChange={(value) => setClientForm({ ...clientForm, client_type: value })}
                          >
                            <SelectTrigger data-testid="new-client-type-select">
                              <SelectValue placeholder="Sélectionner" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="particulier">Particulier</SelectItem>
                              <SelectItem value="professionnel">Professionnel</SelectItem>
                              <SelectItem value="collectivite">Collectivité</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                      </div>
                      <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label htmlFor="client-email">Email</Label>
                          <Input
                            id="client-email"
                            type="email"
                            value={clientForm.email}
                            onChange={(e) => setClientForm({ ...clientForm, email: e.target.value })}
                            data-testid="new-client-email-input"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="client-phone">Téléphone</Label>
                          <Input
                            id="client-phone"
                            value={clientForm.phone}
                            onChange={(e) => setClientForm({ ...clientForm, phone: e.target.value })}
                            data-testid="new-client-phone-input"
                          />
                        </div>
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="client-address">Adresse</Label>
                        <Input
                          id="client-address"
                          value={clientForm.address}
                          onChange={(e) => setClientForm({ ...clientForm, address: e.target.value })}
                          data-testid="new-client-address-input"
                        />
                      </div>
                      <div className="flex justify-end gap-3 pt-4">
                        <Button type="button" variant="outline" onClick={() => setClientDialogOpen(false)}>
                          Annuler
                        </Button>
                        <Button
                          type="submit"
                          className="bg-yellow-500 hover:bg-yellow-600 text-slate-900"
                          disabled={clientFormLoading || !clientForm.name}
                          data-testid="save-new-client-btn"
                        >
                          {clientFormLoading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                          Créer
                        </Button>
                      </div>
                    </form>
                  </DialogContent>
                </Dialog>
              </div>
              </div>
              
              {/* Search input */}
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  type="text"
                  placeholder="Rechercher un contact (nom, email, téléphone, entreprise)..."
                  value={clientSearch}
                  onChange={(e) => setClientSearch(e.target.value)}
                  className="pl-10"
                  data-testid="client-search-input"
                />
              </div>
              
              {/* Client selection - Show list only when searching */}
              {clientSearch.trim() && (
                <div className="max-h-64 overflow-y-auto border rounded-md divide-y">
                  {filteredClients.length === 0 ? (
                    <div className="p-4 text-center text-slate-500">
                      Aucun contact trouvé pour "{clientSearch}"
                    </div>
                  ) : (
                    filteredClients.slice(0, 10).map((client) => (
                      <div
                        key={client.id}
                        onClick={() => {
                          setForm({ ...form, client_id: client.id });
                          setClientSearch(''); // Clear search after selection
                        }}
                        className={cn(
                          'p-3 cursor-pointer transition-colors',
                          form.client_id === client.id
                            ? 'bg-yellow-50 border-l-4 border-l-yellow-500'
                            : 'hover:bg-slate-50'
                        )}
                        data-testid={`client-option-${client.id}`}
                      >
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="font-medium text-slate-800">{client.name}</p>
                            <div className="flex items-center gap-2 text-sm text-slate-500">
                              {client.company && <span>{client.company}</span>}
                              {client.email && <span>{client.email}</span>}
                            </div>
                          </div>
                          {form.client_id === client.id && (
                            <Check className="w-5 h-5 text-yellow-600" />
                          )}
                        </div>
                      </div>
                    ))
                  )}
                </div>
              )}
              
              {/* Selected client display */}
              {selectedClient && (
                <div className="p-4 bg-yellow-50 rounded-lg border-2 border-yellow-200">
                  <div className="flex items-start justify-between">
                    <div>
                      <p className="font-bold text-lg text-slate-800">{selectedClient.name}</p>
                      {selectedClient.company && <p className="text-slate-600">{selectedClient.company}</p>}
                      {selectedClient.client_type && (
                        <span className="inline-block mt-1 px-2 py-0.5 bg-yellow-200 text-yellow-800 text-xs rounded-full capitalize">
                          {selectedClient.client_type}
                        </span>
                      )}
                      <div className="mt-2 space-y-1 text-sm text-slate-600">
                        {selectedClient.email && <p>📧 {selectedClient.email}</p>}
                        {selectedClient.phone && <p>📞 {selectedClient.phone}</p>}
                        {selectedClient.address && <p>📍 {selectedClient.address}</p>}
                      </div>
                    </div>
                    <Button 
                      variant="ghost" 
                      size="sm" 
                      onClick={() => setForm({ ...form, client_id: '' })}
                      className="text-slate-400 hover:text-slate-600"
                    >
                      Changer
                    </Button>
                  </div>
                </div>
              )}
              
              {/* No client selected message */}
              {!selectedClient && !clientSearch.trim() && (
                <div className="p-8 text-center border-2 border-dashed border-slate-200 rounded-lg">
                  <Users className="w-12 h-12 mx-auto text-slate-300 mb-3" />
                  <p className="text-slate-500">Recherchez un contact existant ou créez-en un nouveau</p>
                </div>
              )}
            </div>
          )}

          {/* Step 2: Machine */}
          {currentStep === 2 && (
            <div className="space-y-4" data-testid="step-machine">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                <h2 className="text-xl font-semibold text-slate-800" style={{ fontFamily: 'Barlow Condensed' }}>
                  Sélectionner une mini pelle
                </h2>
                <div className="flex items-center gap-2">
                  {/* Brand filter */}
                  <Select value={selectedBrand} onValueChange={setSelectedBrand}>
                    <SelectTrigger className="w-[180px]" data-testid="brand-filter">
                      <SelectValue placeholder="Toutes les marques" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Toutes les marques</SelectItem>
                      {[...new Set(miniPelles.map(mp => mp.brand).filter(Boolean))].sort().map(brand => (
                        <SelectItem key={brand} value={brand}>{brand}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  
                  {/* View toggle */}
                  <div className="flex items-center gap-1 border rounded-lg p-1 bg-slate-50">
                    <Button
                      variant={machineViewMode === 'dropdown' ? 'default' : 'ghost'}
                      size="sm"
                      onClick={() => setMachineViewMode('dropdown')}
                      className={machineViewMode === 'dropdown' ? 'bg-slate-800' : ''}
                      data-testid="machine-view-dropdown-btn"
                      title="Liste déroulante"
                    >
                      <Search className="w-4 h-4" />
                    </Button>
                    <Button
                      variant={machineViewMode === 'grid' ? 'default' : 'ghost'}
                      size="sm"
                      onClick={() => setMachineViewMode('grid')}
                      className={machineViewMode === 'grid' ? 'bg-slate-800' : ''}
                      data-testid="machine-view-grid-btn"
                      title="Grille"
                    >
                      <LayoutGrid className="w-4 h-4" />
                    </Button>
                    <Button
                      variant={machineViewMode === 'list' ? 'default' : 'ghost'}
                      size="sm"
                      onClick={() => setMachineViewMode('list')}
                      className={machineViewMode === 'list' ? 'bg-slate-800' : ''}
                      data-testid="machine-view-list-btn"
                      title="Liste"
                    >
                      <List className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </div>

              {/* Dropdown View - Quick Selection */}
              {machineViewMode === 'dropdown' && (
                <div className="space-y-4">
                  <div className="bg-slate-50 border rounded-lg p-4">
                    <Label className="text-sm text-slate-600 mb-2 block">Sélection rapide par liste déroulante</Label>
                    <Popover open={machinePopoverOpen} onOpenChange={setMachinePopoverOpen}>
                      <PopoverTrigger asChild>
                        <Button
                          variant="outline"
                          role="combobox"
                          aria-expanded={machinePopoverOpen}
                          className="w-full justify-between bg-white font-normal h-10"
                          data-testid="machine-dropdown-select"
                        >
                          {form.mini_pelle_id
                            ? (() => {
                                const mp = miniPelles.find(m => m.id === form.mini_pelle_id);
                                const t = mp && formatTonnage(mp);
                                return mp ? `${mp.name}${t ? ' — ' + t : ''}` : 'Choisir une machine...';
                              })()
                            : 'Choisir une machine...'}
                          <ChevronsUpDown className="w-4 h-4 ml-2 opacity-50 flex-shrink-0" />
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-[420px] p-0" align="start">
                        <Command>
                          <CommandInput placeholder="Rechercher par nom, marque, tonnage…" className="h-9" />
                          <CommandList className="max-h-[340px]">
                            <CommandEmpty>Aucune machine trouvée.</CommandEmpty>
                            {[...new Set(miniPelles.map(mp => mp.brand || 'Autres'))].sort().map(brand => (
                              <CommandGroup key={brand} heading={brand}>
                                {miniPelles.filter(mp => (mp.brand || 'Autres') === brand).map((mp) => {
                                  const tonnage = formatTonnage(mp);
                                  return (
                                    <CommandItem
                                      key={mp.id}
                                      value={`${mp.name} ${mp.brand || ''} ${tonnage || ''} ${mp.weight || ''}`}
                                      onSelect={() => {
                                        setForm({ ...form, mini_pelle_id: mp.id, selected_options: [] });
                                        setMachinePopoverOpen(false);
                                      }}
                                      className="flex items-center justify-between gap-2 py-2.5 cursor-pointer"
                                      data-testid={`machine-option-${mp.id}`}
                                    >
                                      <div className="flex items-center gap-2 flex-1 min-w-0">
                                        <Check className={`w-4 h-4 flex-shrink-0 ${form.mini_pelle_id === mp.id ? 'opacity-100 text-green-600' : 'opacity-0'}`} />
                                        <span className="font-medium truncate">{mp.name}</span>
                                        {tonnage && (
                                          <span className="text-xs bg-slate-100 text-slate-600 px-1.5 py-0.5 rounded font-mono flex-shrink-0">{tonnage}</span>
                                        )}
                                      </div>
                                      <span className="text-yellow-600 font-mono text-sm flex-shrink-0">{formatPrice(mp.base_price)}</span>
                                    </CommandItem>
                                  );
                                })}
                              </CommandGroup>
                            ))}
                          </CommandList>
                        </Command>
                      </PopoverContent>
                    </Popover>
                  </div>
                  
                  {/* Selected machine preview */}
                  {selectedMiniPelle && (
                    <div className="border-2 border-yellow-500 rounded-lg p-4 bg-yellow-50">
                      <div className="flex items-center gap-4">
                        <div className="w-24 h-20 bg-white rounded-lg overflow-hidden flex-shrink-0">
                          {getImageUrl(selectedMiniPelle.image_url) ? (
                            <img src={getImageUrl(selectedMiniPelle.image_url)} alt={selectedMiniPelle.name} className="w-full h-full object-cover" />
                          ) : (
                            <div className="w-full h-full flex items-center justify-center">
                              <Package className="w-8 h-8 text-slate-300" />
                            </div>
                          )}
                        </div>
                        <div className="flex-1">
                          {selectedMiniPelle.brand && (
                            <p className="text-xs text-yellow-600 font-semibold">{selectedMiniPelle.brand}</p>
                          )}
                          <h3 className="font-bold text-lg text-slate-800">{selectedMiniPelle.name}</h3>
                          <p className="text-sm text-slate-500">{selectedMiniPelle.model}</p>
                          <p className="font-mono text-xl font-bold text-yellow-600 mt-1">
                            {formatPrice(selectedMiniPelle.base_price)}
                          </p>
                        </div>
                        <div className="flex-shrink-0">
                          <div className="w-10 h-10 bg-yellow-500 rounded-full flex items-center justify-center">
                            <Check className="w-6 h-6 text-white" />
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Brand badges - only show for grid/list views */}
              {machineViewMode !== 'dropdown' && (
                <div className="flex flex-wrap gap-2">
                  <Badge 
                    variant={selectedBrand === 'all' ? 'default' : 'outline'}
                    className={cn("cursor-pointer", selectedBrand === 'all' && "bg-yellow-500")}
                    onClick={() => setSelectedBrand('all')}
                  >
                    Toutes ({miniPelles.length})
                  </Badge>
                  {[...new Set(miniPelles.map(mp => mp.brand).filter(Boolean))].sort().map(brand => (
                    <Badge 
                      key={brand}
                      variant={selectedBrand === brand ? 'default' : 'outline'}
                      className={cn("cursor-pointer", selectedBrand === brand && "bg-yellow-500")}
                      onClick={() => setSelectedBrand(brand)}
                    >
                      {brand} ({miniPelles.filter(mp => mp.brand === brand).length})
                    </Badge>
                  ))}
                </div>
              )}

              {/* Filtered machines - only for grid/list views */}
              {machineViewMode !== 'dropdown' && (() => {
                const filteredMachines = selectedBrand === 'all' 
                  ? miniPelles 
                  : miniPelles.filter(mp => mp.brand === selectedBrand);
                
                return (
                  <>
                    {/* Grid View */}
                    {machineViewMode === 'grid' && (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                      {filteredMachines.map((mp) => (
                        <div
                          key={mp.id}
                          onClick={() => setForm({ ...form, mini_pelle_id: mp.id, selected_options: [] })}
                          className={cn(
                            'border rounded-md p-4 cursor-pointer transition-all',
                            form.mini_pelle_id === mp.id
                              ? 'border-yellow-500 bg-yellow-50 ring-2 ring-yellow-500'
                              : 'border-slate-200 hover:border-slate-300'
                          )}
                          data-testid={`select-mp-${mp.id}`}
                        >
                          <div className="aspect-video bg-slate-100 rounded-md mb-3 overflow-hidden">
                            {getImageUrl(mp.image_url) ? (
                              <img src={getImageUrl(mp.image_url)} alt={mp.name} className="w-full h-full object-cover" />
                            ) : (
                              <div className="w-full h-full flex items-center justify-center">
                                <Package className="w-12 h-12 text-slate-300" />
                              </div>
                            )}
                          </div>
                          {mp.brand && <p className="text-xs text-yellow-600 font-semibold">{mp.brand}</p>}
                          <h3 className="font-semibold text-slate-800">{mp.name}</h3>
                          <p className="text-sm text-slate-500">{mp.model}</p>
                          <p className="font-mono text-lg font-bold text-yellow-600 mt-2">
                            {formatPrice(mp.base_price)}
                          </p>
                        </div>
                      ))}
                    </div>
                    )}

                    {/* List View */}
                    {machineViewMode === 'list' && (
                    <div className="space-y-3">
                      {filteredMachines.map((mp) => (
                  <div
                    key={mp.id}
                    onClick={() => setForm({ ...form, mini_pelle_id: mp.id, selected_options: [] })}
                    className={cn(
                      'border rounded-lg p-4 cursor-pointer transition-all',
                      form.mini_pelle_id === mp.id
                        ? 'border-yellow-500 bg-yellow-50 ring-2 ring-yellow-500'
                        : 'border-slate-200 hover:border-slate-300'
                    )}
                    data-testid={`select-mp-list-${mp.id}`}
                  >
                    <div className="flex items-center gap-4">
                      {/* Image */}
                      <div className="w-24 h-20 sm:w-32 sm:h-24 bg-slate-100 rounded-lg overflow-hidden flex-shrink-0">
                        {getImageUrl(mp.image_url) ? (
                          <img src={getImageUrl(mp.image_url)} alt={mp.name} className="w-full h-full object-cover" />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center">
                            <Package className="w-8 h-8 text-slate-300" />
                          </div>
                        )}
                      </div>
                      
                      {/* Info */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-start justify-between gap-2">
                          <div>
                            <h3 className="font-semibold text-slate-800 text-lg" style={{ fontFamily: 'Barlow Condensed' }}>
                              {mp.name}
                            </h3>
                            <p className="text-sm text-slate-500">{mp.model}</p>
                          </div>
                          <p className="font-mono text-lg font-bold text-yellow-600 flex-shrink-0">
                            {formatPrice(mp.base_price)}
                          </p>
                        </div>
                        
                        {mp.description && (
                          <p className="text-sm text-slate-600 mt-1 line-clamp-1 hidden sm:block">{mp.description}</p>
                        )}
                        
                        {/* Specs row */}
                        <div className="flex flex-wrap items-center gap-2 mt-2 text-xs text-slate-500">
                          {mp.weight && (
                            <span className="bg-slate-100 px-2 py-1 rounded">{mp.weight}</span>
                          )}
                          {mp.power && (
                            <span className="bg-slate-100 px-2 py-1 rounded hidden sm:inline">{mp.power}</span>
                          )}
                          {mp.profondeur_fouille && (
                            <span className="bg-slate-100 px-2 py-1 rounded hidden md:inline">Prof: {mp.profondeur_fouille}</span>
                          )}
                        </div>
                      </div>
                      
                      {/* Selection indicator */}
                      {form.mini_pelle_id === mp.id && (
                        <div className="flex-shrink-0">
                          <div className="w-8 h-8 bg-yellow-500 rounded-full flex items-center justify-center">
                            <Check className="w-5 h-5 text-white" />
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
              )}
                  </>
                );
              })()}

              {/* Variant selector */}
              {selectedMiniPelle?.has_variants && selectedMiniPelle.variants?.length > 0 && (
                <div className="mt-6 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                  <h4 className="font-semibold text-slate-800 mb-3 flex items-center gap-2">
                    <span className="w-2 h-2 bg-yellow-500 rounded-full"></span>
                    Choisir une variante pour {selectedMiniPelle.name}
                  </h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    {selectedMiniPelle.variants.map((variant) => (
                      <div
                        key={variant.id}
                        onClick={() => setForm({ ...form, selected_variant_id: variant.id })}
                        className={cn(
                          'border rounded-lg p-3 cursor-pointer transition-all',
                          form.selected_variant_id === variant.id
                            ? 'border-yellow-500 bg-white ring-2 ring-yellow-500'
                            : 'border-slate-200 bg-white hover:border-yellow-300'
                        )}
                      >
                        <div className="flex items-center gap-3">
                          {variant.image_url && (
                            <div className="w-16 h-12 bg-slate-100 rounded overflow-hidden flex-shrink-0">
                              <img src={getImageUrl(variant.image_url)} alt={variant.name} className="w-full h-full object-cover" />
                            </div>
                          )}
                          <div className="flex-1 min-w-0">
                            <p className="font-medium text-slate-800">{variant.name}</p>
                            <p className="font-mono text-yellow-600 font-bold">{formatPrice(variant.price)}</p>
                          </div>
                          {form.selected_variant_id === variant.id && (
                            <div className="w-6 h-6 bg-yellow-500 rounded-full flex items-center justify-center flex-shrink-0">
                              <Check className="w-4 h-4 text-white" />
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Step 3: Options */}
          {currentStep === 3 && (
            <div className="space-y-6" data-testid="step-options">
              <h2 className="text-xl font-semibold text-slate-800" style={{ fontFamily: 'Barlow Condensed' }}>
                Options et accessoires
              </h2>
              
              {categories.map((cat, catIndex) => {
                // Filter options: only show compatible ones for the selected machine
                const catOptions = options.filter((o) => o.category === cat.id && isOptionCompatible(o));
                if (catOptions.length === 0) return null;
                
                // Couleurs alternées pour les catégories (vert, bleu, violet, orange, rose)
                const categoryColors = [
                  { border: 'border-l-emerald-500', bg: 'bg-emerald-50', text: 'text-emerald-700', badge: 'bg-emerald-100 text-emerald-700' },
                  { border: 'border-l-blue-500', bg: 'bg-blue-50', text: 'text-blue-700', badge: 'bg-blue-100 text-blue-700' },
                  { border: 'border-l-violet-500', bg: 'bg-violet-50', text: 'text-violet-700', badge: 'bg-violet-100 text-violet-700' },
                  { border: 'border-l-orange-500', bg: 'bg-orange-50', text: 'text-orange-700', badge: 'bg-orange-100 text-orange-700' },
                  { border: 'border-l-pink-500', bg: 'bg-pink-50', text: 'text-pink-700', badge: 'bg-pink-100 text-pink-700' },
                  { border: 'border-l-cyan-500', bg: 'bg-cyan-50', text: 'text-cyan-700', badge: 'bg-cyan-100 text-cyan-700' },
                ];
                const colorSet = categoryColors[catIndex % categoryColors.length];
                
                return (
                  <div 
                    key={cat.id} 
                    className={cn(
                      "rounded-lg border border-slate-200 overflow-hidden",
                      "border-l-4",
                      colorSet.border
                    )}
                  >
                    {/* Category Header */}
                    <div className={cn("px-4 py-3 flex items-center justify-between", colorSet.bg)}>
                      <h3 className={cn("font-semibold flex items-center gap-2", colorSet.text)}>
                        <Wrench className="w-4 h-4" />
                        {cat.name}
                      </h3>
                      <span className={cn("text-xs px-2 py-1 rounded-full font-medium", colorSet.badge)}>
                        {catOptions.length} option{catOptions.length > 1 ? 's' : ''}
                      </span>
                    </div>
                    
                    {/* Category Options */}
                    <div className="p-4 bg-white">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        {catOptions.map((opt) => {
                          const price = getOptionPrice(opt);
                          const included = isOptionIncluded(opt);
                          const isSelected = form.selected_options.some((o) => o.option_id === opt.id);
                          
                          return (
                            <div
                              key={opt.id}
                              className={cn(
                                'flex items-center gap-3 p-3 border rounded-lg cursor-pointer transition-all',
                                isSelected
                                  ? 'border-yellow-500 bg-yellow-50 shadow-sm'
                                  : 'border-slate-200 hover:border-slate-300 hover:bg-slate-50'
                              )}
                              onClick={() => toggleOption(opt)}
                              data-testid={`toggle-opt-${opt.id}`}
                            >
                              <Checkbox checked={isSelected} className="flex-shrink-0" />
                              {getImageUrl(opt.image_url) ? (
                                <img
                                  src={getImageUrl(opt.image_url)}
                                  alt={opt.name}
                                  className="w-12 h-12 object-cover rounded flex-shrink-0"
                                />
                              ) : (
                                <div className="w-12 h-12 bg-slate-100 rounded flex items-center justify-center flex-shrink-0">
                                  <Wrench className="w-5 h-5 text-slate-400" />
                                </div>
                              )}
                              <div className="flex-1 min-w-0">
                                <p className="font-medium text-slate-800">{opt.name}</p>
                                {opt.description && (
                                  <p className="text-sm text-slate-500 line-clamp-1">{opt.description}</p>
                                )}
                              </div>
                              <span className={cn(
                                "font-mono font-medium text-sm flex-shrink-0",
                                included ? "text-green-600" : "text-slate-800"
                              )}>
                                {included ? '✓ Inclus' : (price > 0 ? formatPrice(price) : 'Gratuit')}
                              </span>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  </div>
                );
              })}
              
              {options.length === 0 && (
                <p className="text-slate-500 text-center py-8">
                  Aucune option disponible pour cette machine
                </p>
              )}
            </div>
          )}

          {/* Step 4: Financement */}
          {currentStep === 4 && (
            <div className="space-y-6" data-testid="step-financement">
              <h2 className="text-xl font-semibold text-slate-800" style={{ fontFamily: 'Barlow Condensed' }}>
                Options de financement
              </h2>
              
              {/* Toggle SNC */}
              <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
                <div className="flex items-center gap-3">
                  <Calculator className="w-5 h-5 text-yellow-600" />
                  <div>
                    <p className="font-medium text-slate-800">Activer le financement SNC</p>
                    <p className="text-sm text-slate-500">Afficher la simulation de financement sur le devis</p>
                  </div>
                </div>
                <Switch
                  checked={form.show_snc}
                  onCheckedChange={(checked) => setForm({ ...form, show_snc: checked })}
                  data-testid="toggle-snc"
                />
              </div>
              
              {form.show_snc && (
                <>
                  {/* Simulation SNC en temps réel */}
                  <div className="border-2 border-yellow-200 rounded-lg overflow-hidden">
                    <div className="bg-yellow-500 text-slate-900 px-4 py-3">
                      <div className="flex items-center gap-2">
                        <Calculator className="w-5 h-5" />
                        <h3 className="font-bold text-lg" style={{ fontFamily: 'Barlow Condensed' }}>
                          SIMULATION FINANCEMENT SNC
                        </h3>
                      </div>
                    </div>
                    <div className="p-4 bg-yellow-50">
                      {/* Apport client */}
                      <div className="mb-4 flex items-center gap-3 bg-white p-3 rounded-lg border border-yellow-200">
                        <Label className="text-sm font-medium text-slate-700 whitespace-nowrap">
                          Apport client :
                        </Label>
                        <div className="flex items-center gap-2">
                          <Input
                            type="number"
                            min="0"
                            max="75"
                            value={form.apport_client_percent}
                            onChange={(e) => setForm({ ...form, apport_client_percent: Math.max(0, Math.min(75, Number(e.target.value) || 0)) })}
                            className="w-20 text-center font-mono"
                            data-testid="apport-client-input"
                          />
                          <span className="text-slate-600 font-medium">%</span>
                          <span className="text-slate-500 text-sm ml-2">
                            = {formatPrice(calculateTotals().total * (form.apport_client_percent / 100))}
                          </span>
                        </div>
                      </div>
                      
                      {(() => {
                        const totals = calculateTotals();
                        const snc = calculateSNC(totals.total);
                        return (
                          <>
                            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                              <div className="bg-white p-4 rounded-lg border border-yellow-200">
                                <p className="text-sm text-slate-500 uppercase tracking-wide mb-1">Apport SNC (25%)</p>
                                <p className="text-xl font-mono font-bold text-slate-800">{formatPrice(snc.apportSNC)}</p>
                              </div>
                              <div className="bg-white p-4 rounded-lg border border-yellow-200">
                                <p className="text-sm text-slate-500 uppercase tracking-wide mb-1">Apport client ({form.apport_client_percent}%)</p>
                                <p className="text-xl font-mono font-bold text-slate-800">{formatPrice(snc.apportClient)}</p>
                              </div>
                              <div className="bg-white p-4 rounded-lg border border-yellow-200">
                                <p className="text-sm text-slate-500 uppercase tracking-wide mb-1">Montant financé</p>
                                <p className="text-xl font-mono font-bold text-slate-800">{formatPrice(snc.totalFinance)}</p>
                              </div>
                              <div className="bg-yellow-500 text-slate-900 p-4 rounded-lg">
                                <p className="text-sm text-yellow-100 uppercase tracking-wide mb-1">Loyer mensuel (60 mois)</p>
                                <p className="text-xl font-mono font-bold">{formatPrice(snc.loyerMensuel)}/mois</p>
                              </div>
                            </div>
                            <p className="text-xs text-slate-500 mt-3 italic">
                              * Simulation indicative sur 60 mois avec coefficient 1.2. Apport total: {formatPrice(snc.totalApport)} ({25 + form.apport_client_percent}%). Sous réserve d'acceptation.
                            </p>
                          </>
                        );
                      })()}
                    </div>
                  </div>
                  
                  {/* Cabinet SNC */}
                  <div className="p-4 bg-slate-50 rounded-lg space-y-3">
                    <Label className="font-medium text-slate-800">Cabinet SNC</Label>
                    <Select
                      value={form.cabinet_snc_id || "none"}
                      onValueChange={(value) => setForm({ ...form, cabinet_snc_id: value === "none" ? "" : value })}
                    >
                      <SelectTrigger data-testid="cabinet-snc-select">
                        <SelectValue placeholder="Sélectionner un cabinet SNC" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">-- Aucun cabinet --</SelectItem>
                        {cabinetsSNC.map((cab) => (
                          <SelectItem key={cab.id} value={cab.id}>
                            {cab.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    {selectedCabinetSNC && (
                      <div className="text-sm text-slate-600 bg-white p-3 rounded border">
                        <p className="font-medium">{selectedCabinetSNC.name}</p>
                        {selectedCabinetSNC.email && <p>📧 {selectedCabinetSNC.email}</p>}
                        {selectedCabinetSNC.phone && <p>📞 {selectedCabinetSNC.phone}</p>}
                      </div>
                    )}
                  </div>
                  
                  {/* Banques - Sélection multiple */}
                  <div className="p-4 bg-slate-50 rounded-lg space-y-3">
                    <Label className="font-medium text-slate-800">Banques partenaires (sélection multiple)</Label>
                    <p className="text-xs text-slate-500">
                      Les banques sélectionnées recevront le devis en copie par email.
                    </p>
                    {banques.length === 0 ? (
                      <p className="text-sm text-slate-500 italic">
                        Aucune banque configurée. Créez un contact avec le type "Banque" dans la section Contacts.
                      </p>
                    ) : (
                      <div className="space-y-2 max-h-48 overflow-y-auto">
                        {banques.map((banque) => {
                          const isSelected = form.selected_banques.some(b => b.id === banque.id);
                          return (
                            <div
                              key={banque.id}
                              onClick={() => {
                                if (isSelected) {
                                  setForm({
                                    ...form,
                                    selected_banques: form.selected_banques.filter(b => b.id !== banque.id)
                                  });
                                } else {
                                  setForm({
                                    ...form,
                                    selected_banques: [...form.selected_banques, {
                                      id: banque.id,
                                      name: banque.name,
                                      email: banque.email
                                    }]
                                  });
                                }
                              }}
                              className={cn(
                                'flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-all',
                                isSelected
                                  ? 'border-yellow-500 bg-yellow-50'
                                  : 'border-slate-200 bg-white hover:border-slate-300'
                              )}
                              data-testid={`banque-option-${banque.id}`}
                            >
                              <Checkbox checked={isSelected} />
                              <div className="flex-1">
                                <p className="font-medium text-slate-800">{banque.name}</p>
                                {banque.email && (
                                  <p className="text-xs text-slate-500">{banque.email}</p>
                                )}
                              </div>
                              {isSelected && <Check className="w-5 h-5 text-yellow-600" />}
                            </div>
                          );
                        })}
                      </div>
                    )}
                    {form.selected_banques.length > 0 && (
                      <div className="mt-2 p-2 bg-yellow-100 rounded text-sm">
                        <span className="font-medium text-yellow-800">
                          {form.selected_banques.length} banque(s) sélectionnée(s)
                        </span>
                        <span className="text-yellow-700"> - recevront le devis en CC</span>
                      </div>
                    )}
                  </div>
                </>
              )}
            </div>
          )}

          {/* Step 5: Summary */}
          {currentStep === 5 && (
            <div className="space-y-6" data-testid="step-summary">
              <h2 className="text-xl font-semibold text-slate-800" style={{ fontFamily: 'Barlow Condensed' }}>
                Récapitulatif du devis
              </h2>
              
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Left: Details */}
                <div className="space-y-4">
                  {/* Client */}
                  <div className="p-4 bg-slate-50 rounded-md">
                    <p className="text-sm text-slate-500 mb-1">Client</p>
                    <p className="font-medium text-slate-800">{selectedClient?.name}</p>
                    {selectedClient?.company && (
                      <p className="text-slate-600">{selectedClient.company}</p>
                    )}
                  </div>
                  
                  {/* Machine */}
                  <div className="p-4 bg-slate-50 rounded-md">
                    <p className="text-sm text-slate-500 mb-2">Machine</p>
                    <div className="flex items-center gap-4">
                      {getImageUrl(selectedMiniPelle?.image_url) && (
                        <img
                          src={getImageUrl(selectedMiniPelle.image_url)}
                          alt={selectedMiniPelle.name}
                          className="w-20 h-20 object-cover rounded-md"
                        />
                      )}
                      <div>
                        <p className="font-medium text-slate-800">{selectedMiniPelle?.name}</p>
                        <p className="text-slate-600">{selectedMiniPelle?.model}</p>
                        <p className="font-mono font-bold text-yellow-600">
                          {formatPrice(selectedMiniPelle?.base_price || 0)}
                        </p>
                      </div>
                    </div>
                  </div>
                  
                  {/* Options */}
                  {form.selected_options.length > 0 && (
                    <div className="p-4 bg-slate-50 rounded-md">
                      <p className="text-sm text-slate-500 mb-2">Options sélectionnées</p>
                      <div className="space-y-2">
                        {form.selected_options.map((opt) => (
                          <div key={opt.option_id} className="flex items-center justify-between">
                            <div className="flex items-center gap-2">
                              {getImageUrl(opt.image_url) && (
                                <img
                                  src={getImageUrl(opt.image_url)}
                                  alt={opt.option_name}
                                  className="w-8 h-8 object-cover rounded"
                                />
                              )}
                              <span className="text-slate-700">{opt.option_name}</span>
                            </div>
                            <span className={cn("font-mono", opt.price === 0 ? "text-green-600" : "text-slate-800")}>
                              {opt.price === 0 ? '✓ Inclus' : formatPrice(opt.price)}
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  
                  {/* Discount & Notes */}
                  <div className="space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>Remise</Label>
                        <Input
                          type="number"
                          min="0"
                          value={form.discount}
                          onChange={(e) => setForm({ ...form, discount: parseFloat(e.target.value) || 0 })}
                          data-testid="discount-input"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Type</Label>
                        <Select
                          value={form.discount_type}
                          onValueChange={(value) => setForm({ ...form, discount_type: value })}
                        >
                          <SelectTrigger data-testid="discount-type-select">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="percent">%</SelectItem>
                            <SelectItem value="fixed">€</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label>Validité (jours)</Label>
                      <Input
                        type="number"
                        min="1"
                        value={form.validity_days}
                        onChange={(e) => setForm({ ...form, validity_days: parseInt(e.target.value) || 30 })}
                        data-testid="validity-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Notes</Label>
                      <Textarea
                        value={form.notes}
                        onChange={(e) => setForm({ ...form, notes: e.target.value })}
                        rows={3}
                        placeholder="Notes internes ou conditions particulières..."
                        data-testid="notes-input"
                      />
                    </div>
                  </div>
                </div>
                
                {/* Right: Totals */}
                <div>
                  <Card className="border-slate-200 sticky top-4">
                    <CardHeader className="border-b border-slate-100 bg-slate-50/50">
                      <CardTitle style={{ fontFamily: 'Barlow Condensed' }}>Total</CardTitle>
                    </CardHeader>
                    <CardContent className="p-6 space-y-3">
                      <div className="flex justify-between text-slate-600">
                        <span>Machine</span>
                        <span className="font-mono">{formatPrice(totals.miniPellePrice)}</span>
                      </div>
                      <div className="flex justify-between text-slate-600">
                        <span>Options ({form.selected_options.length})</span>
                        <span className="font-mono">{formatPrice(totals.optionsTotal)}</span>
                      </div>
                      <div className="border-t border-slate-200 pt-3 flex justify-between">
                        <span className="text-slate-600">Sous-total</span>
                        <span className="font-mono font-medium">{formatPrice(totals.subtotal)}</span>
                      </div>
                      {form.discount > 0 && (
                        <div className="flex justify-between text-green-600">
                          <span>Remise ({form.discount}{form.discount_type === 'percent' ? '%' : '€'})</span>
                          <span className="font-mono">-{formatPrice(totals.discountAmount)}</span>
                        </div>
                      )}
                      <div className="border-t border-slate-200 pt-3 flex justify-between">
                        <span className="text-xl font-bold text-slate-800" style={{ fontFamily: 'Barlow Condensed' }}>
                          TOTAL TTC
                        </span>
                        <span className="text-xl font-mono font-bold text-yellow-600">
                          {formatPrice(totals.total)}
                        </span>
                      </div>
                      
                      {/* Simulation SNC */}
                      {form.show_snc && totals.total > 0 && (
                        <div className="mt-4 pt-4 border-t border-yellow-200">
                          <div className="bg-yellow-50 rounded-lg p-3">
                            <p className="text-xs font-bold text-yellow-700 uppercase tracking-wide mb-2 flex items-center gap-1">
                              <Calculator className="w-3 h-3" />
                              Simulation SNC (sera incluse dans le PDF)
                            </p>
                            {(() => {
                              const snc = calculateSNC(totals.total);
                              return (
                                <div className="space-y-1 text-sm">
                                  <div className="flex justify-between">
                                    <span className="text-slate-600">Apport SNC (25%)</span>
                                    <span className="font-mono text-slate-800">{formatPrice(snc.apportSNC)}</span>
                                  </div>
                                  <div className="flex justify-between">
                                    <span className="text-slate-600">Apport client ({form.apport_client_percent}%)</span>
                                    <span className="font-mono text-slate-800">{formatPrice(snc.apportClient)}</span>
                                  </div>
                                  <div className="flex justify-between">
                                    <span className="text-slate-600">Montant financé</span>
                                    <span className="font-mono text-slate-800">{formatPrice(snc.totalFinance)}</span>
                                  </div>
                                  <div className="flex justify-between font-semibold bg-yellow-100 -mx-3 px-3 py-2 rounded mt-2">
                                    <span className="text-yellow-700">Loyer 60 mois</span>
                                    <span className="font-mono text-yellow-600">{formatPrice(snc.loyerMensuel)}/mois</span>
                                  </div>
                                </div>
                              );
                            })()}
                            {/* Cabinet SNC selection info */}
                            {selectedCabinetSNC && (
                              <div className="mt-3 pt-3 border-t border-yellow-200 flex items-center gap-2">
                                <Building2 className="w-4 h-4 text-yellow-600" />
                                <span className="text-sm text-yellow-700">
                                  Cabinet: <strong>{selectedCabinetSNC.name}</strong>
                                </span>
                              </div>
                            )}
                            {/* Banques info */}
                            {form.selected_banques.length > 0 && (
                              <div className="mt-2 pt-2 border-t border-yellow-200">
                                <div className="flex items-center gap-2 mb-1">
                                  <Building2 className="w-4 h-4 text-yellow-600" />
                                  <span className="text-sm text-yellow-700 font-medium">
                                    {form.selected_banques.length} banque(s) en CC :
                                  </span>
                                </div>
                                <div className="text-xs text-yellow-600 pl-6">
                                  {form.selected_banques.map(b => b.name).join(', ')}
                                </div>
                              </div>
                            )}
                          </div>
                        </div>
                      )}
                      
                      {!form.show_snc && (
                        <div className="mt-4 pt-4 border-t border-slate-200">
                          <p className="text-xs text-slate-400 text-center">
                            La simulation SNC ne sera pas incluse dans le PDF
                          </p>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                </div>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Navigation */}
      <div className="flex justify-between">
        <Button
          variant="outline"
          onClick={() => setCurrentStep((s) => s - 1)}
          disabled={currentStep === 1}
          data-testid="prev-step-btn"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Précédent
        </Button>
        
        {currentStep < 5 ? (
          <Button
            onClick={() => setCurrentStep((s) => s + 1)}
            disabled={!canProceed()}
            className="bg-slate-800 hover:bg-slate-900"
            data-testid="next-step-btn"
          >
            Suivant
            <ArrowRight className="w-4 h-4 ml-2" />
          </Button>
        ) : (
          <Button
            onClick={handleSubmit}
            disabled={submitting}
            className="bg-yellow-500 hover:bg-yellow-600 text-slate-900"
            data-testid="create-devis-submit-btn"
          >
            {submitting && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
            Créer le devis
          </Button>
        )}
      </div>
    </div>
  );
};

export default DevisCreatePage;
